import React from "react";
import { DocumentData } from "../xml-document-parser.service";
import { PartyInfoSection, AddressBlock, InfoGrid } from "./party-info-section.component";
import { ServicesTable, ServicesSummary } from "./services-table.component";

interface KvDocumentViewerProps {
	data: DocumentData;
}

export const KvDocumentViewer: React.FC<KvDocumentViewerProps> = ({ data }) => {
	const formatEmployer = () => {
		if (!data.employer) return '';
		const parts = [];
		if (data.employer.regNumber) parts.push(data.employer.regNumber);
		if (data.employer.companyName) parts.push(data.employer.companyName);
		if (data.employer.department) parts.push(data.employer.department);
		if (data.employer.street) parts.push(data.employer.street);
		if (data.employer.zip || data.employer.city) {
			parts.push(`${data.employer.zip || ''} ${data.employer.city || ''}`.trim());
		}
		return parts.join(' · ');
	};

	const formatAhvNumber = (ahv: string) => {
		// Format AHV number as 756.1234.5678.90
		const cleaned = ahv.replace(/\D/g, '');
		if (cleaned.length >= 13) {
			return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 7)}.${cleaned.slice(7, 11)}.${cleaned.slice(11, 13)}`;
		}
		return ahv;
	};

	const formatReferrer = () => {
		if (!data.referrer) return '';
		const parts = [];
		if (data.referrer.gln || data.referrer.eanParty) {
			parts.push(data.referrer.gln || data.referrer.eanParty);
		}
		if (data.referrer.zsr) {
			parts.push(`/ ${data.referrer.zsr}`);
		}
		if (data.referrer.title) parts.push(data.referrer.title);
		if (data.referrer.givenName && data.referrer.familyName) {
			parts.push(`${data.referrer.givenName} ${data.referrer.familyName}`);
		} else if (data.referrer.name) {
			parts.push(data.referrer.name);
		}
		if (data.referrer.specialty) parts.push(data.referrer.specialty);
		if (data.referrer.street) parts.push(data.referrer.street);
		if (data.referrer.zip || data.referrer.city) {
			parts.push(`${data.referrer.zip || ''} ${data.referrer.city || ''}`.trim());
		}
		return parts.join(' · ');
	};

	return (
		<div className="font-sans text-black">
			{/* Header */}
			<div className="border-b-2 border-black pb-2 mb-4">
				<div className="flex justify-between items-start">
					<div>
						<h1 className="text-xl font-bold">Kostengutsprache-Anfrage</h1>
						<div className="text-xs mt-1">
							<span>Dokument Identifikation: </span>
							<span>{data.timestamp || data.requestTimestamp || ''} · {data.date}</span>
						</div>
					</div>
					<div className="text-right text-xs">
						<div>Release {data.release || ''}GCR/de</div>
						<div>Seite: 1</div>
					</div>
				</div>
			</div>

			{/* Rolle/Ort */}
			{(data.role || data.place) && (
				<div className="text-xs mb-2">
					<span className="font-semibold">Rolle/Ort</span>
					<span className="ml-4">
						{data.role === 'physiotherapist' ? 'PhysiotherapeutIn' : data.role}
						{data.role && data.place && ' · '}
						{data.place === 'practice' ? 'Praxis' : data.place}
					</span>
				</div>
			)}

			{/* Antragsteller / Leistungserbringer */}
			<div className="grid grid-cols-2 gap-6 mb-4 text-xs">
				<div>
					<div className="font-semibold mb-1">Antragssteller</div>
					{data.biller && (
						<>
							<div className="flex">
								<span className="w-20">GLN-Nr.(R)</span>
								<span>{data.biller.gln || data.biller.eanParty || ''}</span>
								<span className="ml-4">{data.biller.companyName}</span>
								{data.biller.department && <span className="ml-2">{data.biller.department}</span>}
							</div>
							<div className="flex">
								<span className="w-20">ZSR-Nr.(R)</span>
								<span>{data.biller.zsr || ''}</span>
								<span className="ml-4">{data.biller.street} · {data.biller.zip} {data.biller.city}</span>
							</div>
							<div className="flex">
								<span className="w-20"></span>
								{data.biller.phone && <span>Tel: {data.biller.phone}</span>}
								{data.biller.fax && <span className="ml-4">Fax: {data.biller.fax}</span>}
							</div>
						</>
					)}
				</div>
				<div>
					<div className="font-semibold mb-1">Leistungserbringer</div>
					{data.provider && (
						<>
							<div className="flex">
								<span className="w-20">GLN-Nr.(P)</span>
								<span>{data.provider.gln || data.provider.eanParty || ''}</span>
								{data.provider.salutation && <span className="ml-4">{data.provider.salutation}</span>}
								{data.provider.title && <span className="ml-2">{data.provider.title}</span>}
								<span className="ml-2">{data.provider.name || `${data.provider.givenName || ''} ${data.provider.familyName || ''}`.trim()}</span>
							</div>
							<div className="flex">
								<span className="w-20">ZSR-Nr.(P)</span>
								<span>{data.provider.zsr || ''}</span>
								<span className="ml-4">{data.provider.street} · {data.provider.zip} {data.provider.city}</span>
							</div>
							<div className="flex">
								<span className="w-20"></span>
								{data.provider.phone && <span>Tel: {data.provider.phone}</span>}
								{data.provider.fax && <span className="ml-4">Fax: {data.provider.fax}</span>}
							</div>
						</>
					)}
				</div>
			</div>

			{/* Main content area with patient info and insurance */}
			<div className="grid grid-cols-3 gap-4 mb-4 text-xs">
				{/* Patient column */}
				<div className="border border-gray-400 p-2">
					<div className="font-semibold mb-2">Patient</div>
					<table className="w-full">
						<tbody>
							<tr><td className="pr-2">Name</td><td className="font-medium">{data.patient?.familyName || ''}</td></tr>
							<tr><td className="pr-2">Vorname</td><td className="font-medium">{data.patient?.givenName || ''}</td></tr>
							<tr><td className="pr-2">Strasse</td><td>{data.patient?.street || ''}</td></tr>
							<tr><td className="pr-2">PLZ</td><td>{data.patient?.zip || ''}</td></tr>
							<tr><td className="pr-2">Ort</td><td>{data.patient?.city || ''}</td></tr>
							<tr><td className="pr-2">Geburtsdatum</td><td>{data.patient?.birthdate || ''}</td></tr>
							<tr><td className="pr-2">Geschlecht</td><td>{data.patient?.gender === 'male' ? 'M' : data.patient?.gender === 'female' ? 'W' : ''}</td></tr>
							{data.law === 'UVG' && data.caseDate && <tr><td className="pr-2">Unfalldatum</td><td>{data.caseDate}</td></tr>}
							{data.law === 'UVG' && data.caseId && <tr><td className="pr-2">Unfall-Nr.</td><td>{data.caseId}</td></tr>}
							{data.patient?.ahvNumber && <tr><td className="pr-2">AHV-Nr.</td><td>{formatAhvNumber(data.patient.ahvNumber)}</td></tr>}
							{data.patient?.vekaNumber && <tr><td className="pr-2">VEKA-Nr.</td><td>{data.patient.vekaNumber}</td></tr>}
							{data.patient?.insuredNumber && <tr><td className="pr-2">Versicherten-Nr.</td><td>{data.patient.insuredNumber}</td></tr>}
							{data.treatment?.canton && <tr><td className="pr-2">Leistungskanton</td><td>{data.treatment.canton}</td></tr>}
							{data.treatment?.dateBegin && <tr><td className="pr-2">Behandlung</td><td>{data.treatment.dateBegin}</td></tr>}
							<tr><td className="pr-2">Gesetz</td><td>{data.law || ''}</td></tr>
							<tr><td className="pr-2">Kopie / Vertraulich</td><td>{data.isCopy ? 'ja' : 'nein'} / {data.isConfidential ? 'vertraulich' : 'nicht vertraulich'}</td></tr>
							{data.treatment?.reason && <tr><td className="pr-2">Behandlungsgrund</td><td>{data.treatment.reason === 'accident' ? 'Unfall' : data.treatment.reason === 'disease' ? 'Krankheit' : data.treatment.reason}</td></tr>}
							<tr><td className="pr-2">Behandlungsart</td><td>{data.treatment?.treatmentType || 'Keine Angabe'}</td></tr>
							{data.treatment?.admissionType && <tr><td className="pr-2">Eintrittsart</td><td>{data.treatment.admissionType}</td></tr>}
						</tbody>
					</table>
					{data.employer && (
						<div className="mt-2">
							<div className="font-semibold">Betriebs-Nr./-Name</div>
							<div className="text-xs">{formatEmployer()}</div>
						</div>
					)}
				</div>

				{/* Middle column - Insurance */}
				<div className="border border-gray-400 p-2">
					<div className="font-semibold mb-2">Versicherung</div>
					{data.insurance && (
						<div>
							<div className="mb-1">GLN-Nr: {data.insurance.gln || data.insurance.eanParty || ''}</div>
							<div>{data.insurance.companyName || ''}</div>
							{data.insurance.department && <div>{data.insurance.department}</div>}
							<div>{data.insurance.street || ''}</div>
							<div>{data.insurance.zip || ''} {data.insurance.city || ''}</div>
							{data.insurance.phone && <div>Tel: {data.insurance.phone}</div>}
							{data.insurance.fax && <div>Fax: {data.insurance.fax}</div>}
						</div>
					)}
				</div>

				{/* Right column - Request info */}
				<div className="border border-gray-400 p-2">
					<table className="w-full">
						<tbody>
							<tr><td className="pr-2">KoGu-Datum/-Nr.</td><td>{data.documentNumber ? `${data.documentNumber} / ${data.date || ''}` : data.date || ''}</td></tr>
							{(data.prescriptionType || data.insuranceClass || data.departmentIndication) && (
								<>
									{data.prescriptionType && <tr><td className="pr-2">Verordnung</td><td>{data.prescriptionType}</td></tr>}
									{data.insuranceClass && <tr><td className="pr-2">Vers.-klasse</td><td>{data.insuranceClass}</td></tr>}
									{data.departmentIndication && <tr><td className="pr-2">Abteilung/Indikation</td><td>{data.departmentIndication}</td></tr>}
								</>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Zuweiser/Chirurg */}
			{data.referrer && (
				<div className="border border-gray-400 p-2 mb-4 text-xs">
					<table className="w-full">
						<tbody>
							<tr>
								<td className="w-20">Zuweiser</td>
								<td>GLN-/ZSR-Nr. {formatReferrer()}</td>
							</tr>
							{data.surgeon && (
								<tr>
									<td className="w-20">Chirurg</td>
									<td>GLN-/ZSR-Nr. {data.surgeon.gln || data.surgeon.zsr || ''} {data.surgeon.name || ''}</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			)}

			{/* Diagnosen */}
			{data.treatment?.diagnoses && data.treatment.diagnoses.length > 0 && (
				<div className="mb-4">
					<div className="border border-gray-400 p-2">
						<table className="w-full text-xs">
							<thead>
								<tr className="border-b">
									<th className="text-left p-1">Diagnosen</th>
									<th className="text-left p-1">Katalog::Code</th>
									<th className="text-left p-1">Bezeichnung</th>
								</tr>
							</thead>
							<tbody>
								{data.treatment.diagnoses.map((diagnosis, index) => (
									<tr key={index}>
										<td className="p-1">{diagnosis.type === 'freetext' ? 'Freitext' : diagnosis.type}</td>
										<td className="p-1">{diagnosis.code || ''}</td>
										<td className="p-1">{diagnosis.text}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Services */}
			<div className="mb-4">
				<div className="font-semibold text-sm mb-2">Leistungen</div>
				<ServicesTable services={data.services} type="kostenvoranschlag" />
				<ServicesSummary
					servicesCount={data.servicesCount}
					servicesTotal={data.servicesTotal}
					subtotal={data.subtotal}
					vatAmount={data.vat?.amount}
					vatRate={data.vat?.rate}
					total={data.total}
					currency={data.currency}
					amountDue={data.amountDue}
				/>
				{/* Disclaimer text */}
				<div className="text-xs mt-2">
					Die Kosten beinhalten alle Zuschläge, auch die gesetzlichen.
				</div>
			</div>

			{/* Massnahmen */}
			{data.measures && (
				<div className="mb-4">
					<div className="font-semibold text-sm mb-1">Massnahmen</div>
					<div className="text-xs text-justify border border-gray-400 p-2">
						{data.measures}
					</div>
				</div>
			)}

			{/* Bemerkungen */}
			{data.remark && (
				<div className="mb-4">
					<div className="font-semibold text-sm mb-1">Bemerkungen</div>
					<div className="text-xs text-justify border border-gray-400 p-2">
						{data.remark}
					</div>
				</div>
			)}
		</div>
	);
};